<template>
  <div>
    <Pane />
    <a-card class="container">
      <!-- 顶部操作栏 -->
      <div class="header-wrapper">
        <a-row type="flex" justify="space-between" align="middle">
          <a-col :span="16">
            <a-space>
              <a-button-group>
                <a-button icon="left" @click="prevMonth" />
                <a-month-picker
                  v-model="selectedMonth"
                  placeholder="选择年月"
                  @change="onMonthChange"
                  style="width: 150px"
                />
                <a-button icon="right" @click="nextMonth" />
              </a-button-group>
              <a-input
                addon-before="填报人"
                :value="userName"
                style="width: 200px"
                disabled
              />
              <a-input
                addon-before="部门"
                :value="deptName"
                style="width: 200px"
                disabled
              />
            </a-space>
          </a-col>
          <a-col :span="8" style="text-align: right">
            <a-button type="primary" icon="plus" @click="showContractSelector">
              添加项目
            </a-button>
          </a-col>
        </a-row>
      </div>

      <!-- 数据表格 -->
      <a-table
        bordered
        :data-source="list"
        :loading="loading"
        :pagination="false"
        :scroll="{ x: 'max-content' }"
      >
        <!-- 客商名称列 -->
        <a-table-column title="客商名称" width="150px" fixed="left">
          <template #default="text, record">
            <span>{{ record.partyName || '--' }}</span>
          </template>
        </a-table-column>

        <!-- 合同名列 -->
        <a-table-column title="合同名" data-index="contractName" width="150px" fixed="left" />
        
        <!-- 收款计划列组 -->
        <a-table-column-group title="收款计划（元）">
          <a-table-column title="累计收款计划" width="130px" align="right">
            <template #default="text, record">
              <span style="font-weight: bold">{{ getCumulativePlanAmount(record) }}</span>
            </template>
          </a-table-column>
          <a-table-column
            v-for="week in weekColumns"
            :key="'plan-' + week.weekNumber"
            :title="`第${week.weekNumber}周`"
            width="100px"
            align="right"
          >
            <template #default="text, record">
              <a-input-number
                v-if="canEditPlanAmount"
                :value="getPlanAmount(record, week.weekNumber)"
                :min="0"
                :precision="2"
                @change="(value) => updatePlanAmount(record, week.weekNumber, value)"
                style="width: 100%"
              />
              <span v-else>{{ getPlanAmount(record, week.weekNumber) }}</span>
            </template>
          </a-table-column>
        </a-table-column-group>
        
        <!-- 实际回款列组 -->
        <a-table-column-group title="实际回款（元）">
          <a-table-column title="累计实际回款" width="130px" align="right">
            <template #default="text, record">
              <span style="font-weight: bold">{{ getCumulativeActualAmount(record) }}</span>
            </template>
          </a-table-column>
          <a-table-column
            v-for="week in weekColumns"
            :key="'actual-' + week.weekNumber"
            :title="`第${week.weekNumber}周`"
            width="100px"
            align="right"
          >
            <template #default="text, record">
              <span>{{ getActualAmount(record, week.weekNumber) }}</span>
            </template>
          </a-table-column>
        </a-table-column-group>
        
        <!-- 进展及计划列组 -->
        <a-table-column-group title="进展及计划">
          <a-table-column title="计划未回收" width="130px" align="right">
            <template #default="text, record">
              <span style="font-weight: bold; color: #ff4d4f">{{ getUncollectedAmount(record) }}</span>
            </template>
          </a-table-column>
          <a-table-column
            v-for="week in weekColumns"
            :key="'progress-' + week.weekNumber"
            :title="`第${week.weekNumber}周`"
            width="150px"
          >
            <template #default="text, record">
              <a-input
                :value="getProgressPlan(record, week.weekNumber)"
                @change="(e) => updateProgressPlan(record, week.weekNumber, e.target.value)"
                placeholder="请输入进展计划"
              />
            </template>
          </a-table-column>
        </a-table-column-group>
      </a-table>

      <!-- 空状态 -->
      <!-- 周日期注释已移除 -->

      <!-- 保存按钮 -->
      <div v-if="list.length > 0" style="margin-top: 16px; text-align: center">
        <a-button type="primary" :loading="saving" @click="saveData">
          保存
        </a-button>
      </div>

      <!-- 日历表格 -->
      <div v-if="weekColumns.length > 0" style="margin-top: 16px">
        <a-card title="月度日历" :bordered="false" size="small">
          <table style="width: 100%; border-collapse: collapse; font-size: 12px">
            <thead>
              <tr style="background: #f5f5f5">
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%">周一</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%">周二</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%">周三</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%">周四</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%">周五</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%; color: #999">周六</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%; color: #999">周日</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(week, weekIndex) in calendarWeeks" :key="weekIndex">
                <td 
                  v-for="(day, dayIndex) in week" 
                  :key="dayIndex"
                  :style="{
                    padding: '8px',
                    border: '1px solid #e8e8e8',
                    textAlign: 'center',
                    backgroundColor: day.isCurrentMonth ? (day.isWeekend ? '#fff9f9' : '#fff') : '#f9f9f9',
                    color: day.isCurrentMonth ? (day.isWeekend ? '#999' : '#333') : '#ccc'
                  }"
                >
                  <div v-if="day.day">
                    <div style="font-weight: bold">{{ day.day }}</div>
                    <div v-if="day.weekNumber" style="font-size: 10px; color: #1890ff">第{{ day.weekNumber }}周</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </a-card>
      </div>
    </a-card>

    <!-- 合同选择器 -->
    <MyContractSelector ref="contractSelector" @change="onContractSelect" />
  </div>
</template>

<script>
import { getCurrentMonthPlan, saveProjectPaymentPlan, getWeeks, generateCollectionJson } from "@/api/finance/project-payment-plan";
import { fetchDetail as fetchContractDetail } from "@/api/contract";
import MyContractSelector from "@/components/my-contract-selector";
import moment from "moment";

export default {
  components: {
    MyContractSelector,
  },

  data() {
    return {
      userName: "",
      deptId: "",
      deptName: "",
      selectedMonth: null,
      currentMonth: "",
      list: [],
      loading: false,
      saving: false,
      weekColumns: [],
      canEditPlanAmount: false,
      calendarWeeks: [],
    };
  },

  computed: {
    yearMonth() {
      if (this.selectedMonth) {
        return moment(this.selectedMonth).format("YYYY-MM");
      }
      return this.currentMonth;
    },
  },

  created() {
    this.userName = this.$store.state.auth?.user?.employeeVo?.name || "";
    this.deptId = this.$store.state.auth?.user?.employeeVo?.deptId || "";
    this.deptName = this.$store.state.auth?.user?.employeeVo?.deptName || "";
    this.initCurrentMonth();
    this.generateWeekColumns(); // 先生成周列
    this.checkEditPermission();
    this.getList();
  },

  methods: {
    // 初始化当前月（上个月20号到本月20号算当前月）
    initCurrentMonth() {
      const now = new Date();
      const currentDay = now.getDate();
      const currentMonth = now.getMonth() + 1; // 0-11, 需要+1
      const currentYear = now.getFullYear();

      // 判断当前日期属于哪个业务月
      // 业务月的定义：上个月20号到本月20号算本月的数据
      if (currentDay >= 20) {
        // 如果当前日期大于等于20号，当前业务月是下个月
        // 比如6月20号，当前业务月是7月
        if (currentMonth === 12) {
          // 如果是12月，下个月是1月，年份要+1
          this.currentMonth = `${currentYear + 1}-01`;
        } else {
          this.currentMonth = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
        }
      } else {
        // 如果当前日期小于20号，当前业务月是本月
        // 比如6月15号，当前业务月是6月
        this.currentMonth = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
      }

      this.selectedMonth = moment(this.currentMonth, "YYYY-MM");
    },

    // 检查是否可以编辑计划金额（放开限制，用于测试）
    checkEditPermission() {
      // 放开编辑限制，用于测试
      // 实际规则应该是：本月8号之前可以填报本月的计划金额
      this.canEditPlanAmount = true;
    },

    // 获取列表数据
    getList() {
      this.loading = true;
      getCurrentMonthPlan(this.yearMonth)
        .then((res) => {
          if (res && Array.isArray(res)) {
            this.list = res;
            this.generateWeekColumns();
            // 根据合同信息补全客商名称
            this.loadPartyNames();
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // 查询单条合同信息，补全客商名称（partyAname）
    fetchPartyName(record) {
      if (!record.contractId) return Promise.resolve();
      return fetchContractDetail(record.contractId)
        .then((contract) => {
          const partyName = (contract && (contract["partyAname"] || contract["partyaname"])) || "";
          this.$set(record, "partyName", partyName);
        })
        .catch(() => {
          this.$set(record, "partyName", "");
        });
    },

    // 根据合同ID查询合同信息，补全客商名称
    loadPartyNames() {
      return Promise.all(this.list.map((record) => this.fetchPartyName(record)));
    },

    // 年月选择变化
    onMonthChange(date) {
      this.selectedMonth = date;
      this.checkEditPermission(); // 重新检查编辑权限
      this.generateWeekColumns(); // 重新生成周列
      this.getList();
    },

    // 生成周列（调用后端接口获取周信息）
    generateWeekColumns() {
      getWeeks(this.yearMonth)
        .then((res) => {
          if (res && Array.isArray(res)) {
            this.weekColumns = res.map((week) => ({
              weekNumber: week.weekNumber,
              title: `第${week.weekNumber}周`,
              dateRange: week.dateRange,
              startDate: week.startDate,
              endDate: week.endDate,
            }));
            // 生成日历表格
            this.generateCalendarWeeks();
          }
        })
        .catch((err) => {
          this.$message.error("获取周信息失败：" + (err.message || "未知错误"));
        });
    },

    // 显示合同选择器
    showContractSelector() {
      this.$refs.contractSelector.visible = true;
    },

    // 合同选择
    onContractSelect(contract) {
      console.log('选择的合同对象:', contract);
      console.log('合同ID:', contract.id);
      console.log('合同名:', contract.name);
      
      // 检查是否已经添加过该合同
      const exists = this.list.find(item => item.contractId === contract.id);
      if (exists) {
        this.$message.warning('该合同已添加，请勿重复添加');
        return;
      }

      // 添加新的项目回款计划（客商名称取自合同的 partyAname）
      const partyName = contract["partyAname"] || contract["partyaname"] || "";
      const newPlan = {
        id: null,
        yearMonth: this.yearMonth,
        contractId: contract.id,
        contractName: contract.name,
        partyName,
        paymentPlanList: this.weekColumns.map((week) => ({
          weekNumber: week.weekNumber,
          planAmount: 0,
          actualAmount: 0,
          progressPlan: "",
          gapAmount: 0,
        })),
        planAmount: 0,
        actualAmount: 0,
        gapAmount: 0,
      };

      console.log('新增的计划对象:', newPlan);
      console.log('当前list:', this.list);
      
      // 使用Vue.set确保响应式更新
      this.$set(this.list, this.list.length, newPlan);
      
      console.log('更新后的list:', this.list);
      this.$message.success('添加成功');

      // 如果合同选择器未返回客商名称，再查询合同信息补全
      if (!partyName) {
        this.fetchPartyName(newPlan);
      }

      // 主动查询回款数据并按日期分配到对应周
      this.fetchCollectionRecords(newPlan);
    },

    // 获取回款记录并分配到对应周的实际回款
    fetchCollectionRecords(plan) {
      generateCollectionJson({ contractId: plan.contractId })
        .then((res) => {
          if (res && Array.isArray(res)) {
            res.forEach((item) => {
              // 期初回款无日期，跳过
              if (item.type === 'INITIAL' || !item.date) return;
              const weekNumber = this.getWeekNumberByDate(item.date);
              if (weekNumber !== null) {
                const week = plan.paymentPlanList.find((w) => w.weekNumber === weekNumber);
                if (week) {
                  week.actualAmount = (Number(week.actualAmount) || 0) + (Number(item.amount) || 0);
                }
              }
            });
            this.calculateSummary(plan);
          }
        })
        .catch((err) => {
          this.$message.error('获取回款数据失败：' + (err.message || '未知错误'));
        });
    },

    // 格式化日期为 yyyy-MM-dd（仅比较年月日，避免时区问题）
    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      if (isNaN(d.getTime())) return null;
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    // 根据回款日期判断属于第几周，不在本月周范围内返回 null
    getWeekNumberByDate(date) {
      const target = this.formatDate(date);
      if (!target) return null;
      for (const week of this.weekColumns) {
        const start = this.formatDate(week.startDate);
        const end = this.formatDate(week.endDate);
        if (start && end && target >= start && target <= end) {
          return week.weekNumber;
        }
      }
      return null;
    },

    // 获取计划金额
    getPlanAmount(record, weekNumber) {
      if (record.paymentPlanList) {
        const week = record.paymentPlanList.find((w) => w.weekNumber === weekNumber);
        return week ? week.planAmount || 0 : 0;
      }
      return 0;
    },

    // 更新计划金额
    updatePlanAmount(record, weekNumber, value) {
      if (record.paymentPlanList) {
        const week = record.paymentPlanList.find((w) => w.weekNumber === weekNumber);
        if (week) {
          week.planAmount = value || 0;
          this.calculateSummary(record);
        }
      }
    },

    // 获取实际金额
    getActualAmount(record, weekNumber) {
      if (record.paymentPlanList) {
        const week = record.paymentPlanList.find((w) => w.weekNumber === weekNumber);
        return week ? week.actualAmount || 0 : 0;
      }
      return 0;
    },

    // 获取进展计划
    getProgressPlan(record, weekNumber) {
      if (record.paymentPlanList) {
        const week = record.paymentPlanList.find((w) => w.weekNumber === weekNumber);
        return week ? week.progressPlan || "" : "";
      }
      return "";
    },

    // 更新进展计划
    updateProgressPlan(record, weekNumber, value) {
      if (record.paymentPlanList) {
        const week = record.paymentPlanList.find((w) => w.weekNumber === weekNumber);
        if (week) {
          week.progressPlan = value || "";
        }
      }
    },

    // 四舍五入保留两位小数
    round2(num) {
      return Math.round((num + Number.EPSILON) * 100) / 100;
    },

    // 获取累计收款计划（所有周的收款计划之和，随输入动态更新）
    getCumulativePlanAmount(record) {
      if (record.paymentPlanList) {
        return this.round2(
          record.paymentPlanList.reduce((sum, week) => sum + (Number(week.planAmount) || 0), 0)
        );
      }
      return 0;
    },

    // 获取累计实际回款（所有周的实际回款之和）
    getCumulativeActualAmount(record) {
      if (record.paymentPlanList) {
        return this.round2(
          record.paymentPlanList.reduce((sum, week) => sum + (Number(week.actualAmount) || 0), 0)
        );
      }
      return 0;
    },

    // 获取计划未回收（累计收款计划 - 累计实际回款）
    getUncollectedAmount(record) {
      return this.round2(this.getCumulativePlanAmount(record) - this.getCumulativeActualAmount(record));
    },

    // 计算汇总数据
    calculateSummary(record) {
      if (record.paymentPlanList) {
        record.planAmount = record.paymentPlanList.reduce((sum, week) => sum + (week.planAmount || 0), 0);
        record.actualAmount = record.paymentPlanList.reduce((sum, week) => sum + (week.actualAmount || 0), 0);
        record.gapAmount = record.actualAmount - record.planAmount;
      }
    },

    // 保存数据
    saveData() {
      this.saving = true;

      // 构建批量保存的数据列表
      const dataList = this.list.map((plan) => {
        return {
          id: plan.id,
          yearMonth: this.yearMonth, // 使用当前选择的年月
          deptId: this.deptId, // 使用用户所属部门ID
          deptName: this.deptName, // 使用用户所属部门名称
          contractId: plan.contractId,
          contractName: plan.contractName,
          paymentPlanList: plan.paymentPlanList,
        };
      });

      // 一次性保存所有数据
      saveProjectPaymentPlan(dataList)
        .then(() => {
          this.$message.success("保存成功");
        })
        .catch((err) => {
          this.$message.error("保存失败：" + (err.message || "未知错误"));
        })
        .finally(() => {
          this.saving = false;
        });
    },

    // 生成日历表格
    generateCalendarWeeks() {
      const [year, month] = this.yearMonth.split("-").map(Number);
      const firstDay = new Date(year, month - 1, 1);
      const lastDay = new Date(year, month, 0);
      
      // 获取这个月的第一天是周几（0=周日, 1=周一, ..., 6=周六）
      const firstDayOfWeek = firstDay.getDay();
      
      // 计算日历的第一天（可能是上个月的最后几天）
      const startDay = new Date(firstDay);
      // 如果第一天不是周一，需要向前调整到周一
      if (firstDayOfWeek !== 1) {
        const daysToSubtract = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
        startDay.setDate(startDay.getDate() - daysToSubtract);
      }
      
      // 计算日历的最后一天（可能是下个月的最初几天）
      const endDay = new Date(lastDay);
      // 如果最后一天不是周日，需要向后调整到周日
      const lastDayOfWeek = lastDay.getDay();
      if (lastDayOfWeek !== 0) {
        const daysToAdd = 7 - lastDayOfWeek;
        endDay.setDate(endDay.getDate() + daysToAdd);
      }
      
      // 生成日历周数组
      const weeks = [];
      let currentDay = new Date(startDay);
      
      while (currentDay <= endDay) {
        const week = [];
        for (let i = 0; i < 7; i++) {
          const dayOfMonth = currentDay.getDate();
          const isCurrentMonth = currentDay.getMonth() === month - 1;
          const dayOfWeek = currentDay.getDay();
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          
          // 判断这一天属于哪一周
          let weekNumber = null;
          if (isCurrentMonth) {
            // 查找weekColumns中对应的周
            for (const weekInfo of this.weekColumns) {
              const startDate = new Date(weekInfo.startDate);
              const endDate = new Date(weekInfo.endDate);
              if (currentDay >= startDate && currentDay <= endDate) {
                weekNumber = weekInfo.weekNumber;
                break;
              }
            }
          }
          
          week.push({
            day: dayOfMonth,
            isCurrentMonth: isCurrentMonth,
            isWeekend: isWeekend,
            weekNumber: weekNumber,
          });
          
          currentDay.setDate(currentDay.getDate() + 1);
        }
        weeks.push(week);
      }
      
      this.calendarWeeks = weeks;
    },

    // 上个月
    prevMonth() {
      const [year, month] = this.yearMonth.split("-").map(Number);
      const prevMonthDate = new Date(year, month - 2, 1); // month-2是因为month是1-12，需要减1才能得到正确的索引
      this.selectedMonth = moment(prevMonthDate);
      this.checkEditPermission(); // 重新检查编辑权限
      this.generateWeekColumns();
      this.getList();
    },

    // 下个月
    nextMonth() {
      const [year, month] = this.yearMonth.split("-").map(Number);
      const nextMonthDate = new Date(year, month, 1); // month是因为month是1-12，不需要减1
      this.selectedMonth = moment(nextMonthDate);
      this.checkEditPermission(); // 重新检查编辑权限
      this.generateWeekColumns();
      this.getList();
    },
  },
};
</script>

<style scoped>
.container {
  padding: 16px;
}

.header-wrapper {
  padding: 16px 0;
}

.user-name {
  font-size: 16px;
  font-weight: bold;
}
</style>